import { kvGet, kvSet, kvRemove, kvSetPersisted } from '../infrastructure/storage/kvAccess';
import { getCurrentBusinessId as resolveCurrentBusinessId, normalizeBusinessStorageId } from '../utils/sharedCore';
import {
  normalizeLegacyCashRegisterData,
  isCorruptedKvString,
  parseKvObject
} from '../utils/cashRegisterDataStorage';

// Cash Register Service for local storage operations
class CashRegisterService {
  constructor() {
    this.storageKey = 'cashRegisterData';
  }

  legacyCashRegisterStorageKeys(businessId) {
    if (businessId == null || businessId === '') return [this.storageKey];
    const raw = String(businessId);
    const normalized = normalizeBusinessStorageId(businessId);
    const keys = [];
    const add = (k) => {
      if (k && !keys.includes(k)) keys.push(k);
    };
    add(`${this.storageKey}_${normalized}`);
    add(`${this.storageKey}_${raw}`);
    if (!raw.startsWith('business_')) add(`${this.storageKey}_business_${raw}`);
    if (normalized !== raw) add(`${this.storageKey}_business_${normalized}`);
    return keys;
  }

  getStorageKey(businessId) {
    if (businessId == null || businessId === '') return this.storageKey;
    return `${this.storageKey}_${normalizeBusinessStorageId(businessId)}`;
  }

  // Get cash register data for a specific business
  getCashRegisterData(businessId = null) {
    try {
      const keys = this.legacyCashRegisterStorageKeys(businessId);
      for (const key of keys) {
        const raw = kvGet(key);
        if (raw == null) continue;
        if (typeof raw === 'string' && isCorruptedKvString(raw)) {
          console.warn('⚠️ [CASH REGISTER] Corrupted legacy data, cleaning up');
          kvRemove(key);
          continue;
        }
        const { corrupted, data } = parseKvObject(raw);
        if (corrupted || data == null) continue;
        const normalized = normalizeLegacyCashRegisterData(data);
        const canonical = this.getStorageKey(businessId);
        if (key !== canonical) {
          kvSet(canonical, normalized);
        }
        return normalized;
      }
      return null;
    } catch (error) {
      console.error('Error getting cash register data:', error);
      return null;
    }
  }

  // Save cash register data for a specific business
  saveCashRegisterData(data, businessId = null) {
    try {
      kvSet(this.getStorageKey(businessId), data);
      return true;
    } catch (error) {
      console.error('Error saving cash register data:', error);
      return false;
    }
  }

  async saveCashRegisterDataPersisted(data, businessId = null) {
    try {
      await kvSetPersisted(this.getStorageKey(businessId), data);
      return true;
    } catch (error) {
      console.error('Error persisting cash register data:', error);
      return false;
    }
  }

  /**
   * Schließt die Legacy-Kassensitzung beim Logout — auch bei verwaisten Resten alter Switch-Logik.
   */
  async closeWorkerSessionOnLogout(workerId, cashAmount, businessId = null) {
    try {
      const existingData = this.getCashRegisterData(businessId) || {
        sessions: [],
        activeSession: null,
        lastUpdated: new Date().toISOString()
      };
      const nowIso = new Date().toISOString();
      let changed = false;

      if (existingData.activeSession) {
        const active = existingData.activeSession;
        if (String(active.workerId) === String(workerId)) {
          active.isActive = false;
          active.logoutTime = nowIso;
          active.finalCashAmount = cashAmount;
          active.cashAmount = cashAmount;
          if (!existingData.sessions) existingData.sessions = [];
          existingData.sessions.push(active);
          existingData.activeSession = null;
          changed = true;
        } else {
          active.isActive = false;
          active.logoutTime = nowIso;
          active.finalCashAmount =
            active.finalCashAmount ?? active.cashAmount ?? active.initialAmount ?? 0;
          if (!existingData.sessions) existingData.sessions = [];
          existingData.sessions.push(active);
          existingData.activeSession = null;
          changed = true;
        }
      }

      if (changed) {
        existingData.lastUpdated = nowIso;
        await this.saveCashRegisterDataPersisted(existingData, businessId);
        return { success: true, closed: true };
      }

      return { success: true, closed: false };
    } catch (error) {
      console.error('Error closing legacy cash register session:', error);
      return { success: false, error: error.message };
    }
  }

  // Record cash amount when worker logs in
  recordWorkerLogin(workerId, workerName, cashAmount, businessId = null) {
    try {
      const existingData = this.getCashRegisterData(businessId) || {};
      const sessionId = this.generateSessionId();
      
      // Get predecessor information
      let predecessorInfo = null;
      if (existingData.activeSession) {
        predecessorInfo = {
          workerId: existingData.activeSession.workerId,
          workerName: existingData.activeSession.workerName,
          finalCashAmount: existingData.activeSession.finalCashAmount || existingData.activeSession.cashAmount,
          logoutTime: new Date().toISOString()
        };
      } else if (existingData.sessions && existingData.sessions.length > 0) {
        // Get the most recent completed session
        const recentSession = existingData.sessions
          .filter(s => s.logoutTime)
          .sort((a, b) => new Date(b.logoutTime) - new Date(a.logoutTime))[0];
        
        if (recentSession) {
          predecessorInfo = {
            workerId: recentSession.workerId,
            workerName: recentSession.workerName,
            finalCashAmount: recentSession.finalCashAmount || recentSession.cashAmount,
            logoutTime: recentSession.logoutTime
          };
        }
      }
      
      const newSession = {
        sessionId,
        workerId,
        workerName,
        loginTime: new Date().toISOString(),
        initialAmount: cashAmount || 0, // Primary field for initial amount
        cashAmount: cashAmount || null, // Keep for backward compatibility
        finalCashAmount: null, // Will be set on logout
        predecessorInfo: predecessorInfo,
        isActive: true
      };

      if (existingData.activeSession) {
        if (String(existingData.activeSession.workerId) !== String(workerId)) {
          return {
            success: false,
            error: 'Another worker session is still active — logout required first'
          };
        }
        existingData.activeSession.isActive = false;
        existingData.activeSession.logoutTime = new Date().toISOString();
        if (!existingData.sessions) existingData.sessions = [];
        existingData.sessions.push(existingData.activeSession);
      }

      existingData.activeSession = newSession;
      existingData.lastUpdated = new Date().toISOString();

      this.saveCashRegisterData(existingData, businessId);
      return { success: true, sessionId };
    } catch (error) {
      console.error('Error recording worker login:', error);
      return { success: false, error: error.message };
    }
  }

  // Record cash amount when worker logs out
  recordWorkerLogout(workerId, cashAmount, businessId = null) {
    try {
      const existingData = this.getCashRegisterData(businessId);
      if (!existingData || !existingData.activeSession) {
        return { success: false, error: 'No active session found' };
      }

      if (String(existingData.activeSession.workerId) !== String(workerId)) {
        return { success: false, error: 'Session mismatch' };
      }

      // Update active session with logout info
      existingData.activeSession.isActive = false;
      existingData.activeSession.logoutTime = new Date().toISOString();
      existingData.activeSession.finalCashAmount = cashAmount; // Primary field for final amount
      existingData.activeSession.cashAmount = cashAmount; // Keep for backward compatibility

      // Move to sessions history
      if (!existingData.sessions) existingData.sessions = [];
      existingData.sessions.push(existingData.activeSession);

      // Clear active session
      existingData.activeSession = null;
      existingData.lastUpdated = new Date().toISOString();

      this.saveCashRegisterData(existingData, businessId);
      return { success: true };
    } catch (error) {
      console.error('Error recording worker logout:', error);
      return { success: false, error: error.message };
    }
  }

  // Get the previous session's cash amount
  getPreviousSessionCashAmount(businessId = null) {
    try {
      const data = this.getCashRegisterData(businessId);
      const sessions = Array.isArray(data?.sessions) ? data.sessions : [];
      if (!data || sessions.length === 0) {
        return null;
      }

      const completedSessions = sessions.filter((s) => s && !s.isActive);
      if (completedSessions.length === 0) {
        return null;
      }

      // Sort by logout time and get the most recent
      const sortedSessions = completedSessions.sort((a, b) => 
        new Date(b.logoutTime) - new Date(a.logoutTime)
      );

      // Use primary field with fallback for backward compatibility
      const session = sortedSessions[0];
      return session.finalCashAmount || session.cashAmount || null;
    } catch (error) {
      console.error('Error getting previous session cash amount:', error);
      return null;
    }
  }

  // Get current active session
  getActiveSession(businessId = null) {
    try {
      const data = this.getCashRegisterData(businessId);
      return data?.activeSession || null;
    } catch (error) {
      console.error('Error getting active session:', error);
      return null;
    }
  }

  // Get all sessions history
  getSessionsHistory(businessId = null) {
    try {
      const data = this.getCashRegisterData(businessId);
      return data?.sessions || [];
    } catch (error) {
      console.error('Error getting sessions history:', error);
      return [];
    }
  }

  // Get cash differences analysis
  getCashDifferences(businessId = null) {
    try {
      const data = this.getCashRegisterData(businessId);
      const sessions = Array.isArray(data?.sessions) ? data.sessions : [];
      if (!data || sessions.length === 0) return [];

      return sessions
        .filter(session => 
          session.initialAmount !== undefined && 
          session.finalCashAmount !== undefined &&
          !session.isActive
        )
        .map(session => {
          const expectedAmount = (session.initialAmount || 0) + (session.totalSales || 0);
          const actualAmount = session.finalCashAmount || 0;
          const difference = actualAmount - expectedAmount;
          
          return {
            ...session,
            expectedAmount,
            actualAmount,
            difference,
            hasDifference: Math.abs(difference) > 0.01, // Allow for small rounding differences
            differenceType: difference > 0 ? 'surplus' : difference < 0 ? 'shortage' : 'exact'
          };
        })
        .filter(session => session.hasDifference)
        .sort((a, b) => new Date(b.logoutTime) - new Date(a.logoutTime));
    } catch (error) {
      console.error('Error getting cash differences:', error);
      return [];
    }
  }

  // Get cash register summary statistics
  getCashRegisterSummary(businessId = null) {
    try {
      const data = this.getCashRegisterData(businessId);
      const sessions = Array.isArray(data?.sessions) ? data.sessions : [];
      if (!data || sessions.length === 0) {
        return {
          totalSessions: 0,
          totalDifferences: 0,
          totalSurplus: 0,
          totalShortage: 0,
          averageDifference: 0,
          lastUpdated: null
        };
      }

      const differences = this.getCashDifferences(businessId);
      const totalSessions = sessions.filter((s) => s && !s.isActive).length;
      const totalDifferences = differences.length;
      
      const totalSurplus = differences
        .filter(d => d.differenceType === 'surplus')
        .reduce((sum, d) => sum + d.difference, 0);
      
      const totalShortage = Math.abs(differences
        .filter(d => d.differenceType === 'shortage')
        .reduce((sum, d) => sum + d.difference, 0));

      const averageDifference = totalDifferences > 0 ? 
        differences.reduce((sum, d) => sum + d.difference, 0) / totalDifferences : 0;

      return {
        totalSessions,
        totalDifferences,
        totalSurplus,
        totalShortage,
        averageDifference,
        lastUpdated: data.lastUpdated
      };
    } catch (error) {
      console.error('Error getting cash register summary:', error);
      return {
        totalSessions: 0,
        totalDifferences: 0,
        totalSurplus: 0,
        totalShortage: 0,
        averageDifference: 0,
        lastUpdated: null
      };
    }
  }

  // Record cash difference resolution
  recordCashDifferenceResolution(sessionId, resolution, businessId = null) {
    try {
      const data = this.getCashRegisterData(businessId);
      if (!data || !data.sessions) return { success: false, error: 'No sessions found' };

      const sessionIndex = data.sessions.findIndex(s => s.sessionId === sessionId);
      if (sessionIndex === -1) {
        return { success: false, error: 'Session not found' };
      }

      // Add resolution to session
      data.sessions[sessionIndex].resolution = {
        ...resolution,
        resolvedAt: new Date().toISOString(),
        resolvedBy: 'manager' // Could be enhanced to track who resolved it
      };

      data.lastUpdated = new Date().toISOString();
      this.saveCashRegisterData(data, businessId);
      
      return { success: true };
    } catch (error) {
      console.error('Error recording cash difference resolution:', error);
      return { success: false, error: error.message };
    }
  }

  // Clear all cash register data for a business
  clearCashRegisterData(businessId = null) {
    try {
      const key = businessId ? `${this.storageKey}_${businessId}` : this.storageKey;
      kvRemove(key);
      return true;
    } catch (error) {
      console.error('Error clearing cash register data:', error);
      return false;
    }
  }

  // Generate unique session ID
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Get business ID from current user or business data
  getCurrentBusinessId() {
    try {
      return resolveCurrentBusinessId();
    } catch (error) {
      console.error('Error getting current business ID:', error);
      return null;
    }
  }
}

// Create singleton instance
const cashRegisterService = new CashRegisterService();

export default cashRegisterService;
